class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.onUpsellClick.bind(this));
    this.addEventListener('click', this.onUpsellCarouselClick.bind(this));
    this.addEventListener('click', this.onSubscriptionClick.bind(this));
    this.addEventListener('scroll', this.onUpsellCarouselScroll.bind(this), true);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.freeShippingReached = this.isFreeShippingReached();
    this.setHeaderCartIconAccessibility();
    this.updateUpsellCarousels();
    window.addEventListener('resize', this.updateUpsellCarousels.bind(this), { passive: true });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(() => this.handleFreeShippingState());
      });
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    this.handleFreeShippingState();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.handleFreeShippingState();
    requestAnimationFrame(() => this.updateUpsellCarousels());

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  onUpsellClick(event) {
    const button = event.target.closest('[data-cart-drawer-upsell]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    this.addUpsellItem(button);
  }

  onUpsellCarouselClick(event) {
    const button = event.target.closest('[data-cart-upsell-scroll]');
    if (!button || !this.contains(button) || button.disabled) return;

    const track = button.closest('[data-cart-upsell-carousel]')?.querySelector('[data-cart-upsell-track]');
    const firstCard = track?.querySelector('.cart-drawer__gellies-upsell-card');
    if (!track || !firstCard) return;

    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const direction = button.dataset.cartUpsellScroll === 'next' ? 1 : -1;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    track.scrollBy({ left: direction * (firstCard.getBoundingClientRect().width + gap), behavior });
  }

  onUpsellCarouselScroll(event) {
    const track = event.target.closest?.('[data-cart-upsell-track]');
    if (track && this.contains(track)) this.updateUpsellCarousel(track);
  }

  updateUpsellCarousels() {
    this.querySelectorAll('[data-cart-upsell-track]').forEach((track) => this.updateUpsellCarousel(track));
  }

  updateUpsellCarousel(track) {
    const carousel = track.closest('[data-cart-upsell-carousel]');
    const controls = carousel?.querySelector('[data-cart-upsell-controls]');
    if (!controls) return;

    const hasOverflow = track.scrollWidth > track.clientWidth + 2;
    controls.hidden = !hasOverflow;

    const previous = controls.querySelector('[data-cart-upsell-scroll="previous"]');
    const next = controls.querySelector('[data-cart-upsell-scroll="next"]');
    if (previous) previous.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
  }

  onSubscriptionClick(event) {
    const revealButton = event.target.closest('[data-cart-subscription-reveal]');
    if (revealButton && this.contains(revealButton)) {
      const offer = revealButton.closest('[data-cart-subscription-offer]');
      const controls = offer?.querySelector('[data-cart-subscription-controls]');

      if (!offer || !controls) return;

      const shouldOpen = controls.hidden;
      revealButton.setAttribute('aria-expanded', String(shouldOpen));
      controls.hidden = !shouldOpen;
      return;
    }

    const editButton = event.target.closest('[data-cart-subscription-edit]');
    if (editButton && this.contains(editButton)) {
      const editorId = editButton.getAttribute('aria-controls');
      const editor = editorId ? this.querySelector(`#${CSS.escape(editorId)}`) : null;

      if (!editor) return;

      const shouldOpen = editor.hidden;
      editor.hidden = !shouldOpen;
      editButton.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

      if (shouldOpen) {
        editor.querySelector('[data-cart-subscription-select]')?.focus();
      }
      return;
    }

    const confirmButton = event.target.closest('[data-cart-subscription-confirm]');
    if (!confirmButton || !this.contains(confirmButton)) return;

    event.preventDefault();
    this.changeSellingPlan(confirmButton);
  }

  changeSellingPlan(button) {
    const container = button.closest('[data-cart-subscription-offer], [data-cart-subscription-editor]');
    const select = container?.querySelector('[data-cart-subscription-select]');
    const lineKey = container?.dataset.lineKey;
    const planId = select?.value;
    const quantity = Number.parseInt(container?.dataset.quantity, 10);

    if (!container || !select || !lineKey || !planId || !Number.isFinite(quantity)) return;

    const originalLabel = button.textContent;
    const loadingLabel = button.dataset.loadingLabel || originalLabel;
    const sections = this.getSectionsToRender().map((section) => section.id);
    const body = JSON.stringify({
      id: lineKey,
      quantity,
      selling_plan: Number(planId),
      sections,
      sections_url: window.location.pathname,
    });

    this.setActiveElement(button);
    this.showCartError('');
    container.classList.add('is-loading');
    button.disabled = true;
    button.textContent = loadingLabel;

    fetch(`${routes.cart_change_url}`, { ...fetchConfig('javascript'), body })
      .then(async (response) => {
        const parsedState = await response.json();

        if (!response.ok || parsedState.errors || parsedState.status) {
          const message = parsedState.description || parsedState.message || parsedState.errors || window.cartStrings?.error;
          throw new Error(typeof message === 'string' ? message : window.cartStrings?.error);
        }

        return parsedState;
      })
      .then((parsedState) => {
        this.renderContents(parsedState);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-subscription',
            cartData: parsedState,
          });
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: parsedState }));
      })
      .catch((error) => {
        console.error(error);
        this.showCartError(error.message || window.cartStrings?.error);
      })
      .finally(() => {
        container.classList.remove('is-loading');
        button.disabled = false;
        button.textContent = originalLabel;
      });
  }

  addUpsellItem(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('aria-disabled', true);
    button.classList.add('loading');
    this.setActiveElement(button);

    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.showCartError(response.description || response.message || window.cartStrings?.error);
          return;
        }

        this.showCartError('');
        this.renderContents(response);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-upsell',
            productVariantId: variantId,
            cartData: response,
          });
        }
      })
      .catch((error) => {
        console.error(error);
        this.showCartError(window.cartStrings?.error || error.message);
      })
      .finally(() => {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      });
  }

  showCartError(message) {
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = message || '';
  }

  isFreeShippingReached() {
    return this.querySelector('[data-free-shipping-reached]')?.dataset.freeShippingReached === 'true';
  }

  handleFreeShippingState() {
    const reached = this.isFreeShippingReached();
    if (reached && this.freeShippingReached === false) this.launchConfetti();
    this.freeShippingReached = reached;
  }

  launchConfetti() {
    const canvas = this.querySelector('#CartDrawer-Confetti') || document.getElementById('CartDrawer-Confetti');
    if (!canvas || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const colors = ['#74bdee', '#9dbdff', '#51b280', '#f3d9bd', '#2e496a'];
    const particleCount = width < 750 ? 58 : 120;
    const particles = Array.from({ length: particleCount }, () => ({
      x: width * (0.58 + Math.random() * 0.34),
      y: height * (0.14 + Math.random() * 0.22),
      size: 5 + Math.random() * 7,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * Math.PI,
      speedX: -4 + Math.random() * 8,
      speedY: -7 - Math.random() * 5,
      gravity: 0.28 + Math.random() * 0.16,
      opacity: 1,
    }));

    let frame = 0;
    const totalFrames = 110;
    canvas.width = width;
    canvas.height = height;
    canvas.style.display = 'block';

    const draw = () => {
      context.clearRect(0, 0, width, height);

      particles.forEach((particle) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        particle.speedY += particle.gravity;
        particle.rotation += 0.18;
        particle.opacity = Math.max(0, 1 - frame / totalFrames);

        context.save();
        context.globalAlpha = particle.opacity;
        context.translate(particle.x, particle.y);
        context.rotate(particle.rotation);
        context.fillStyle = particle.color;
        context.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size * 0.62);
        context.restore();
      });

      frame += 1;
      if (frame <= totalFrames) {
        requestAnimationFrame(draw);
      } else {
        context.clearRect(0, 0, width, height);
        canvas.style.display = 'none';
      }
    };

    draw();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
